/** @jsxImportSource @emotion/react */
import { Interpolation, Theme } from '@emotion/react';
import { TextField as MuiTextField, InputAdornment } from '@mui/material';
import { debounce } from 'lodash';
import {
  useState,
  ChangeEventHandler,
  KeyboardEventHandler,
  useEffect,
  Ref,
  ReactNode,
  useMemo,
} from 'react';
import { createStyles, IconName, useTheme } from '../../';
import { Icon } from '../Icon';

export interface BaseProps {
  value: string;
  label?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
  icon?: IconName;
  startAdornment?: ReactNode;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement>;
  endAdornment?: ReactNode;
  className?: string;
  css?: Interpolation<Theme>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  testId?: string;
  turnAutoCompleteOff?: boolean;
}

export interface InputProps extends BaseProps {
  type?: 'number' | 'password' | 'text' | 'tel' | 'email';
}

interface TextAreaProps extends BaseProps {
  type: 'textarea';
  rows?: number;
}

/**
 * Generict input elemnt
 * @param type Defaults to text
 */
export const Input = (props: InputProps | TextAreaProps) => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [showError, setShowError] = useState(false);

  const {
    value,
    label,
    onChange,
    onKeyDown,
    onKeyUp,
    icon,
    error,
    helperText,
    type = 'text',
    disabled,
    placeholder,
    inputRef,
    startAdornment,
    endAdornment,
    onFocus,
    onBlur,
    testId,
    css,
    turnAutoCompleteOff,
  } = props;

  const debounErrorMessage = useMemo(
    () =>
      debounce(() => {
        if (error && helperText) setShowError(true);
        else setShowError(false);
      }, 1000),
    [error, helperText]
  );

  useEffect(() => {
    debounErrorMessage();
  }, [error, helperText, value, debounErrorMessage]);

  const HelperText = () => {
    if (!helperText) return null;

    const element = (
      <span css={styles.helperTextContainer}>
        <Icon name={error ? 'error' : 'info'} size={spacing.xxs} />
        <small css={styles.helperText}>{helperText}</small>
      </span>
    );
    if (!error) return element;
    if (error && showError) return element;
    else return null;
  };

  const labelRightTranslate = icon ? '48px' : '16px';
  const labelDownTranslate = value === '' ? '16px' : '10px';
  const labelScale = value === '' ? '1' : '0.5';
  const labelColor =
    value === '' ? palette.grey.default : palette.primary.default;

  const labelTransform = {
    '.MuiInputLabel-root': {
      color: palette.grey.default,
      transform: `translate(${labelRightTranslate}, ${labelDownTranslate}) scale(${labelScale})`,
      '&.Mui-focused': {
        color: error && showError ? palette.danger.default : labelColor,
      },
    },
  };

  const startAdornmentProps = icon
    ? {
      startAdornment: (
        <InputAdornment position="start">
          <Icon name={icon} />
        </InputAdornment>
      ),
    }
    : startAdornment
      ? {
        startAdornment: (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
      }
      : {};

  return (
    <MuiTextField
      className={props.className}
      variant="outlined"
      label={label}
      type={showPassword ? 'text' : type}
      color="primary"
      disabled={disabled}
      autoComplete={turnAutoCompleteOff ? 'off' : undefined}
      multiline={type === 'textarea' ? true : undefined}
      rows={props.type === 'textarea' ? props.rows : undefined}
      fullWidth
      value={value}
      onChange={(e) => {
        debounErrorMessage.cancel();
        setShowError(false);
        onChange && onChange(e);
      }}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      error={error && showError}
      helperText={!disabled && <HelperText />}
      css={[
        styles.default,
        disabled,
        labelTransform,
        label && (!icon ? styles.activeLabel : styles.activeLabelWithIcon),
        css,
      ]}
      onFocus={onFocus}
      onBlur={onBlur}
      inputProps={{
        'data-testid': testId,
      }}
      InputProps={{
        endAdornment: endAdornment ? (
          <InputAdornment position="end" css={styles.endAdornment}>
            {endAdornment}
          </InputAdornment>
        ) : undefined,
        notched: false,
        ...startAdornmentProps,
        ...(type === 'password'
          ? {
            endAdornment: (
              <InputAdornment
                position="end"
                onClick={() => setShowPassword(!showPassword)}
                css={styles.visibility}
                data-testid="showPassword"
              >
                <Icon
                  name={showPassword ? 'visibility-on' : 'visibility-off'}
                />
              </InputAdornment>
            ),
          }
          : {}),
      }}
      placeholder={placeholder}
      inputRef={inputRef}
    />
  );
};

const useStyles = createStyles(({ palette, spacing, typography }) => ({
  default: {
    '.MuiOutlinedInput-root': {
      background: palette.white,
      '&.Mui-error > .MuiOutlinedInput-notchedOutline': {
        borderColor: palette.danger.x500,
      },
      '&.Mui-focused > .MuiOutlinedInput-notchedOutline': {
        borderWidth: 1,
      },
      '&:not(.Mui-focused):not(.Mui-error) > .MuiOutlinedInput-notchedOutline':
      {
        borderColor: palette.grey.x200,
        borderWidth: 1,
      },

      input: {
        padding: spacing.xxs,
        ...typography.body,
        letterSpacing: 'normal',
        height: 'unset',
      },
    },
    '.MuiFormHelperText-root': {
      marginLeft: '0',
    },
  },
  disabled: {
    '.MuiOutlinedInput-root': {
      background: palette.grey.x100,
    },
  },
  activeLabel: {
    '.MuiOutlinedInput-root': {
      input: { padding: `22px ${spacing.xxs} 10px ${spacing.xxs}` },
    },
  },
  activeLabelWithIcon: {
    '.MuiOutlinedInput-root': {
      input: { padding: `22px ${spacing.xxs} 10px 0px` },
    },
  },
  helperTextContainer: {
    display: 'flex',
    marginLeft: 0,
    alignItems: 'center',
  },
  helperText: {
    marginLeft: `${spacing.xxxs}`,
    ...typography.caption,
  },
  visibility: {
    cursor: 'pointer',
  },
  endAdornment: {
    gap: spacing.xxs,
  },
}));
